import { useParams } from 'react-router-dom';
import { useStatusGet } from '../hooks/use-status.hook';
import { memo, useMemo } from 'react';
import StatusDetailsComponent from '../components/status-details.component';
import { DetailItem } from '../../../../common/RenderDetails';
import { Status } from '../types/status.types';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatDate } from '@utils/date.util';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import PageTitle from '@components/common/PageTitle';

const StatusDetailsContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data, isLoading, error } = useStatusGet(id || '');

  const prepareDetailsData = useMemo(
    () =>
      (dataObj: Status | undefined): DetailItem[] => {
        if (!dataObj) return [];

        const detailItems: DetailItem[] = [];
        // prepare data to display in the details card
        Object.entries(dataObj).forEach(([key, value]) => {
          if (key === 'isDeleted' || key === 'deletedAt') return;
          if (key === 'createdAt' || key === 'updatedAt') {
            detailItems.push({
              label: t(`common.${key}.label`),
              value: formatDate(value),
            });
            return;
          }

          if (key === 'id') {
            detailItems.push({
              label: t(`common.${key}.label`),
              value: value.toString(),
            });
            return;
          }

          if (key === 'tenantId') {
            detailItems.push({
              label: t(`statuses.${key}.label`),
              value: value.toString(),
            });
            return;
          }

          detailItems.push({
            label: t(`statuses.${key}.label`),
            value: String(value),
          });
        });

        return detailItems;
      },
    [t]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('common.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.details.title', { entity: t('statuses.title') })} />
        <HeaderRow
          title={t('crud.details.title', { entity: t('statuses.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.details.title', { entity: t('statuses.title') })} />
      <HeaderRow
        title={t('crud.details.title', { entity: t('statuses.title') })}
        showBackButton={true}
        trailTarget={id}
      />
      <div className="row mt-3">
        <StatusDetailsComponent
          data={prepareDetailsData(data?.data)}
          success={data?.success ?? false}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(memo(StatusDetailsContainer), 'status.view');
